// After FIREFLY_IDLE_ROAM_TIMEOUT_MS of no pointer/keyboard/scroll
// activity, the dock firefly roams (desktop: near cursor; mobile: screen).
// Reactivation (click, key, scroll, touch) returns to anchor; desktop
// mousemove while roaming follows the cursor without exiting.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  FIREFLY_IDLE_ROAM_TIMEOUT_MS,
  FIREFLY_MOBILE_ROAM_STEP_MS,
  isCoarsePointerDevice,
  randomMobileRoamPoint,
} from "./fireflyIdleRoaming";

export function useFireflyIdleRoaming(options: {
  enabled: boolean;
  anchorScreenX: number;
  anchorScreenY: number;
}) {
  const { enabled, anchorScreenX, anchorScreenY } = options;

  const [roaming, setRoaming] = useState(false);
  const [viewportX, setViewportX] = useState(anchorScreenX);
  const [viewportY, setViewportY] = useState(anchorScreenY);

  const lastPointerRef = useRef({ x: anchorScreenX, y: anchorScreenY });
  const roamingRef = useRef(false);
  const prevEnabledRef = useRef(enabled);
  const idleTimerRef = useRef<number | undefined>(undefined);
  const mobileStepRef = useRef<number | undefined>(undefined);

  roamingRef.current = roaming;

  const snapViewportToAnchor = useCallback(() => {
    setViewportX(anchorScreenX);
    setViewportY(anchorScreenY);
    lastPointerRef.current = { x: anchorScreenX, y: anchorScreenY };
  }, [anchorScreenX, anchorScreenY]);

  const clearIdleTimer = () => {
    if (idleTimerRef.current !== undefined) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = undefined;
    }
  };

  const clearMobileStep = () => {
    if (mobileStepRef.current !== undefined) {
      window.clearInterval(mobileStepRef.current);
      mobileStepRef.current = undefined;
    }
  };

  const exitRoaming = useCallback(() => {
    clearMobileStep();
    setRoaming(false);
    snapViewportToAnchor();
  }, [snapViewportToAnchor]);

  const scheduleIdle = useCallback(() => {
    clearIdleTimer();
    if (!enabled) return;
    idleTimerRef.current = window.setTimeout(() => {
      setRoaming(true);
      if (isCoarsePointerDevice()) {
        const pt = randomMobileRoamPoint();
        setViewportX(pt.x);
        setViewportY(pt.y);
      } else {
        setViewportX(lastPointerRef.current.x);
        setViewportY(lastPointerRef.current.y);
      }
    }, FIREFLY_IDLE_ROAM_TIMEOUT_MS);
  }, [enabled]);

  const reactivate = useCallback(() => {
    if (roamingRef.current) {
      exitRoaming();
    }
    scheduleIdle();
  }, [exitRoaming, scheduleIdle]);

  useEffect(() => {
    const reEnabled = !prevEnabledRef.current && enabled;
    prevEnabledRef.current = enabled;

    if (!enabled) {
      clearIdleTimer();
      clearMobileStep();
      setRoaming(false);
      snapViewportToAnchor();
      return;
    }

    if (reEnabled) {
      clearIdleTimer();
      clearMobileStep();
      setRoaming(false);
      snapViewportToAnchor();
    }

    const onMouseMove = (e: MouseEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      if (roamingRef.current && !isCoarsePointerDevice()) {
        setViewportX(e.clientX);
        setViewportY(e.clientY);
        return;
      }
      if (!roamingRef.current) {
        scheduleIdle();
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) {
        lastPointerRef.current = { x: t.clientX, y: t.clientY };
      }
      reactivate();
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mousedown", reactivate);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("keydown", reactivate);
    window.addEventListener("wheel", reactivate, { passive: true });
    window.addEventListener("scroll", reactivate, { passive: true });

    scheduleIdle();

    return () => {
      clearIdleTimer();
      clearMobileStep();
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mousedown", reactivate);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("keydown", reactivate);
      window.removeEventListener("wheel", reactivate);
      window.removeEventListener("scroll", reactivate);
    };
  }, [enabled, reactivate, scheduleIdle, snapViewportToAnchor]);

  useEffect(() => {
    if (!roaming || !enabled) {
      clearMobileStep();
      return;
    }
    if (!isCoarsePointerDevice()) return;

    mobileStepRef.current = window.setInterval(() => {
      const pt = randomMobileRoamPoint();
      setViewportX(pt.x);
      setViewportY(pt.y);
    }, FIREFLY_MOBILE_ROAM_STEP_MS);

    return clearMobileStep;
  }, [roaming, enabled]);

  useEffect(() => {
    if (!roaming) {
      setViewportX(anchorScreenX);
      setViewportY(anchorScreenY);
    }
  }, [anchorScreenX, anchorScreenY, roaming]);

  return { roaming, viewportX, viewportY };
}
